import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import DocumentModal from '../common/DocumentModal.js';
import inventorySKUService from '../../services/InventorySKUService.js';
import { Package, Pencil, ArrowLeft } from "lucide-react";

const ViewInventorySKU = () => {
  const { skuId } = useParams();
  const [sku, setSku] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showImageModal, setShowImageModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSKU();
  }, [skuId]);

  const fetchSKU = async () => {
    try {
      const response = await inventorySKUService.getById(skuId);
      setSku(response.data);
    } catch (err) {
      Alert.error('Failed to load SKU');
      console.error(err);
      navigate('/inventory');
    } finally {
      setLoading(false);
    }
  };

  const getSKUImageUrl = () => {
    if (sku.sku_image_base64) {
      return `data:image/jpeg;base64,${sku.sku_image_base64}`;
    }
    return null;
  };

  const getStatusBadgeClass = (isActive) => {
    return isActive 
      ? 'bg-green-100 text-green-700' 
      : 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatCurrency = (value) => {
    if (!value) return '₹0.00';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value);
  };

  if (loading) {
    return (
      <div className="max-w-[1850px] mx-auto">
        <Header />
        <div className="flex">
          <Sidebar />
          <div className="flex bg-light w-full mt-16 sm:mt-0">
            <div className="flex-1 flex flex-col overflow-hidden">
              <main className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b border-primary mx-auto mb-4"></div>
                  <p className="text-sm sm:text-base text-gray-600">Loading SKU...</p>
                </div>
              </main>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!sku) {
    return null;
  }

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-4 sm:mb-6">
                  <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                    <Package className="w-5 h-5 text-buttonBg" />SKU Details
                  </h1>
                </div>

                {/* Main Content */}
                <div className="bg-white rounded-sm shadow-sm overflow-hidden">
                  {/* Header Section */}
                  <div className="bg-primary p-3 px-4">
                    <div className="flex flex-col lg:flex-row items-start lg:items-center gap-3 lg:gap-4">
                      <div className="flex-1 min-w-0 flex flex-wrap items-center gap-3">
                        <h2 className="text-xl sm:text-lg md:text-lg font-semibold text-secondary mb-0 break-words">
                          {sku.sku_name}
                        </h2>
                        <span className={`px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-semibold ${getStatusBadgeClass(sku.is_active)}`}>
                          {sku.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="w-full lg:w-auto flex flex-col sm:flex-row gap-2 sm:gap-3">
                        <button
                          onClick={() => navigate(`/inventory-sku/edit/${skuId}`)}
                          className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonBg bg-buttonTxt hover:text-buttonTxt 
hover:bg-buttonBg border border-current rounded-sm  transition-all duration-300 font-medium whitespace-nowrap"
                        >
                          <Pencil className="w-4 h-4" />
                          Edit SKU
                        </button>
                        <button
                          onClick={() => sku.inventory_info?.inventory_id ? navigate(`/inventory/view/${sku.inventory_info.inventory_id}`) : navigate('/inventory')}
                          className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonBg bg-buttonTxt hover:text-buttonTxt 
hover:bg-buttonBg border border-current rounded-sm  transition-all duration-300 font-medium whitespace-nowrap"
                        >
                          <ArrowLeft className="w-4 h-4" />
                          Back to Inventory
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Details Sections */}
                  <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">

                    {/* Parent Inventory Information */}
                    {sku.inventory_info && (
                      <div>
                        <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                          Parent Inventory Item
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Inventory ID</label>
                            <button
                              onClick={() => navigate(`/inventory/view/${sku.inventory_info.inventory_id}`)}
                              className="text-sm sm:text-base text-secondary hover:underline font-semibold"
                            >
                              {sku.inventory_info.inventory_id}
                            </button>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Item Name</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-all">{sku.inventory_info.item_name}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Category</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-all">{sku.inventory_info.category}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Unit</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-all">{sku.inventory_info.unit}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Basic Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        SKU Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">SKU ID</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{sku.sku_id}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">SKU Code</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{sku.sku_code}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">SKU Name</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{sku.sku_name}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Status</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(sku.is_active)}`}>
                              {sku.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Variant Information */}
                    {(sku.variant_type || sku.variant_value) && (
                      <div>
                        <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                          Variant Information
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Variant Type</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-all">{sku.variant_type || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Variant Value</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-all">{sku.variant_value || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Quantity and Pricing */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Quantity and Pricing
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Quantity</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">
                            {sku.quantity} {sku.inventory_info?.unit || 'units'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Unit Price</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatCurrency(sku.unit_price)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Stock Status</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${sku.is_out_of_stock ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                              {sku.is_out_of_stock ? 'Out of Stock' : 'In Stock'}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Storage Location */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Storage Location
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Warehouse</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">
                            {sku.inventory_info?.warehouse_location || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Bin Location</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{sku.bin_location || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    {/* SKU Image */}
                    {sku.sku_image_base64 && (
                      <div>
                        <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                          SKU Image
                        </h3>
                        <button
                          onClick={() => setShowImageModal(true)}
                          className="flex items-center gap-2 px-3 py-2 text-xs sm:text-sm text-buttonTxt bg-buttonBg hover:text-buttonhoverTxt hover:bg-buttonhoverBg border rounded-sm transition-all duration-300 font-medium"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View SKU Image
                        </button>
                      </div>
                    )}

                    {/* Additional Information */}
                    {sku.notes && (
                      <div>
                        <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                          Additional Information
                        </h3>
                        <div className="grid grid-cols-1 gap-4 sm:gap-6">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Notes</label>
                            <p className="text-sm sm:text-base text-secondary font-semibold break-all">{sku.notes}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* System Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        System Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Created By</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{sku.created_by || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Created At</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(sku.created_at)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Updated By</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{sku.updated_by || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Last Updated</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(sku.updated_at)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>

      {/* Document Modal */}
      <DocumentModal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        documentUrl={getSKUImageUrl()}
        documentType="SKU Image"
      />
    </div>
  );
};

export default ViewInventorySKU;